import * as React from "react";
import {useState} from "react";
import {useParams} from "react-router-dom";
import {AppFrame} from "@/app/AppFrame";
import {CircularProgress, Grid2 as Grid, Grid2} from "@mui/material";
import {useModelState} from "@/lib/modelstate";
import {UserPasswordResetDTO} from "@/api/data-contracts";
import {authenticationApi} from "@/boxhub-api";
import {AxiosError} from "axios";
import {FinishedPage, FinishedPageProps} from "@/common/FinishedPage";
import {useTranslation} from "react-i18next";
import {UserI18N} from "@/translations";
import {CardEx} from "@/lib/components/CardEx";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {responsiveGridSize} from "@/lib/screen-class";
import * as yup from "yup";
import {Constants} from "@/api/constants";
import {PASSWORD_REGEX} from "@/regex";
import {encodePasswordBase64} from "@/lib/utils";

export const PasswordResetPage = () => {
    const { t } = useTranslation();
    const params = useParams();
    const [skipUnsavedCheck, setSkipUnsavedCheck] = useState(false);

    const [finishedInfo, setFinishedInfo] = React.useState<FinishedPageProps | null>(null);

    const schema = yup.object().shape({
        password: yup
            .string()
                .required(t(UserI18N.form.validation.passwordRequired))
            .min(
                Constants.PASSWORD_LENGTH_MIN,
                    t(UserI18N.form.validation.passwordMin, {min: Constants.PASSWORD_LENGTH_MIN})
            )
            .max(
                Constants.PASSWORD_LENGTH_MAX,
                    t(UserI18N.form.validation.passwordMax, {max: Constants.PASSWORD_LENGTH_MAX})
            )
                .matches(PASSWORD_REGEX, t(UserI18N.form.validation.passwordInvalid)),

        confirmPassword: yup
            .string()
                .required(t(UserI18N.form.validation.passwordRequired))
            .min(
                Constants.PASSWORD_LENGTH_MIN,
                    t(UserI18N.form.validation.passwordMin, {min: Constants.PASSWORD_LENGTH_MIN})
            )
            .max(
                Constants.PASSWORD_LENGTH_MAX,
                    t(UserI18N.form.validation.passwordMax, {max: Constants.PASSWORD_LENGTH_MAX})
            )
                .oneOf([yup.ref("password")], t(UserI18N.form.validation.passwordsMustMatch))
    }) as yup.ObjectSchema<UserPasswordResetDTO>;

    const pageTitle = t(UserI18N.form.page.resetPasswordTitle);

    const onLoadError = (error: AxiosError<unknown>) => {
        const data = error.response?.data as { error?: { message?: string } } | undefined;
        setFinishedInfo({
            success: false,
            title: t(UserI18N.form.message.passwordResetFailedTitle),
            subTitle: data?.error?.message ?? t(UserI18N.form.message.passwordResetFailedSubtitle)
        });
        return true;
    };

    const { model, errors, onChange, status, save, isDirty } = useModelState<UserPasswordResetDTO, string>({
        initialState: {} as UserPasswordResetDTO,
        schema,
        id: params.token,
        loader: authenticationApi.beginPasswordReset,
        updater: authenticationApi.performPasswordReset,
        sendTransformer: (model) => ({
            ...model,
            password: encodePasswordBase64(model.password),
            confirmPassword: encodePasswordBase64(model.confirmPassword)
        }),
        onLoadError
    });

    const handleSubmit = async () => {
        setSkipUnsavedCheck(true);
        try {
            const result = save();
            if (result === null) {
                return;
            }
            await result;
            setFinishedInfo({
                success: true,
                title: t(UserI18N.form.message.passwordResetSuccessTitle),
                subTitle: t(UserI18N.form.message.passwordResetSuccessSubtitle)
            });
        } catch (error: unknown) {
            const axiosError = error as AxiosError<{ error?: { message?: string } }>;
            setFinishedInfo({
                success: false,
                title: t(UserI18N.form.message.passwordResetFailedTitle),
                subTitle: axiosError.response?.data?.error?.message ?? t(UserI18N.form.message.passwordResetFailedSubtitle)
            });
        }
    };

    if (finishedInfo) {
        return <FinishedPage {...finishedInfo} />;
    }

    return (
        <AppFrame title={pageTitle} modelState={status} isDirty={!skipUnsavedCheck && isDirty}>
            <Grid2 container spacing={2} paddingTop={1}>
                <Grid2 size={{ xs: 12 }}>
                    <CardEx title={t(UserI18N.form.page.resetPasswordSubtitle)}>
                        <Grid2 container direction="column" spacing={3}>
                            <Grid2 container direction="row" spacing={2} width="100%">
                                <Grid size={responsiveGridSize({ md: 4, lg: 3 })}>
                                    <StringInputField
                                        name="password"
                                        type="password"
                                        label={t(UserI18N.form.field.password)}
                                        value={model?.password}
                                        error={errors?.password}
                                        maxChars={Constants.PASSWORD_LENGTH_MAX}
                                        onChange={onChange}
                                    />
                                </Grid>
                            </Grid2>
                            <Grid2 container direction="row" spacing={2} width="100%">
                                <Grid size={responsiveGridSize({ md: 4, lg: 3 })}>
                                    <StringInputField
                                        name="confirmPassword"
                                        type="password"
                                        label={t(UserI18N.form.field.confirmPassword)}
                                        value={model?.confirmPassword}
                                        error={errors?.confirmPassword}
                                        maxChars={Constants.PASSWORD_LENGTH_MAX}
                                        onChange={onChange}
                                    />
                                </Grid>
                            </Grid2>
                            <Grid2 container direction="row" spacing={2} width="100%">
                                <Grid2 size={responsiveGridSize({ md: 4, lg: 3 })}>
                                    <ButtonEx
                                        label={
                                            status === "saving" ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                    t(UserI18N.form.field.resetPassword)
                                            )
                                        }
                                        type="submit"
                                        fullWidth
                                        onClick={handleSubmit}
                                        sx={{ mt: 3, mb: 2 }}
                                    />
                                </Grid2>
                            </Grid2>
                        </Grid2>
                    </CardEx>
                </Grid2>
            </Grid2>
        </AppFrame>
    );
};
